import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CourseCard from "../components/CourseCard";
import AudiencePill from "../components/AudiencePill";

const CATS = [
  { slug: "", label: "All" },
  { slug: "marketing", label: "Marketing" },
  { slug: "sales", label: "Sales" },
  { slug: "service", label: "Service" },
  { slug: "operations", label: "Operations" },
  { slug: "executive-mindset", label: "Executive Mindset" },
];

const DIFFS = ["All", "Beginner", "Intermediate", "Advanced", "Expert"];

const AUDS = [
  { slug: "automotive", label: "Automotive" },
  { slug: "business", label: "Business" },
  { slug: "entrepreneur", label: "Entrepreneur" },
];

export default function Courses() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = searchParams.get("category") || "";
  const difficulty = searchParams.get("difficulty") || "All";
  // audience: explicit param wins; otherwise default to user's preference if logged in
  const audienceParam = searchParams.get("audience");
  const audience = audienceParam || user?.audience || "all";

  useEffect(() => {
    setLoading(true);
    const params = { include_coming_soon: true };  // Phase 16.2 — catalog page shows LIVE + coming-soon with badges
    if (category) params.category = category;
    if (difficulty && difficulty !== "All") params.difficulty = difficulty;
    if (audience && audience !== "all") params.audience = audience;
    api
      .get("/courses", { params })
      .then((r) => setCourses(r.data))
      .catch((e) => console.error("Failed to load courses", e))
      .finally(() => setLoading(false));
  }, [category, difficulty, audience]);

  const updateParam = (key, value, sentinel = "") => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === sentinel) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const setCat = (slug) => updateParam("category", slug);
  const setDiff = (d) => updateParam("difficulty", d, "All");
  const setAud = (slug) => updateParam("audience", slug, "all");

  const showAll = audience === "all";

  return (
    <div className="bg-fc-navy min-h-screen text-white">
      <Navbar />
      <main className="pt-32 pb-24" data-testid="courses-page">
        <section className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="fc-overline mb-3">CATALOG</div>
            <h1 className="fc-h-section text-4xl sm:text-5xl lg:text-6xl mb-4">
              All <span className="text-fc-red">programs</span>.
            </h1>
            <p className="text-white/65 max-w-2xl text-lg">
              Every course on Freddy. Filter by audience, department, or difficulty.
            </p>
          </div>

          {/* AUDIENCE TOGGLE */}
          <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="audience-filter">
            <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-white/45">Audience</span>
            <button
              onClick={() => setAud("all")}
              className={`text-xs tracking-[0.18em] font-bold uppercase px-3 py-1.5 border transition-colors ${
                showAll ? "border-fc-red bg-fc-red text-white" : "border-white/15 text-white/70 hover:border-white/40"
              }`}
              data-testid="audience-filter-all"
            >
              Show All Audiences
            </button>
            {AUDS.map((a) => (
              <button
                key={a.slug}
                onClick={() => setAud(a.slug)}
                className={`text-xs tracking-[0.18em] font-bold uppercase px-3 py-1.5 border transition-colors ${
                  audience === a.slug ? "border-fc-red bg-fc-red text-white" : "border-white/15 text-white/70 hover:border-white/40"
                }`}
                data-testid={`audience-filter-${a.slug}`}
              >
                {a.label}
              </button>
            ))}
            {user?.audience && !audienceParam && (
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/40">
                · Filtered to your preference
              </span>
            )}
          </div>

          {/* CATEGORY CHIPS */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="category-filter">
            {CATS.map((c) => (
              <button
                key={c.slug || "all"}
                onClick={() => setCat(c.slug)}
                className={`text-xs tracking-[0.18em] font-bold uppercase px-4 py-2 border transition-colors ${
                  category === c.slug
                    ? "border-fc-red bg-fc-red text-white"
                    : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
                }`}
                data-testid={`filter-cat-${c.slug || "all"}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* DIFFICULTY PILLS */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="difficulty-filter">
            {DIFFS.map((d) => (
              <button
                key={d}
                onClick={() => setDiff(d)}
                className={`text-[11px] tracking-[0.18em] font-bold uppercase px-3 py-1.5 rounded-full border transition-colors ${
                  difficulty === d
                    ? "border-white/70 bg-white/10 text-white"
                    : "border-white/15 text-white/55 hover:border-white/40 hover:text-white"
                }`}
                data-testid={`filter-diff-${d}`}
              >
                {d}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-white/50">Loading…</p>
          ) : courses.length === 0 ? (
            <p className="text-white/50" data-testid="no-courses-found">No courses match those filters.</p>
          ) : (
            <div className="grid gap-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
              {[...courses].sort((a, b) => Number(!!a.is_coming_soon) - Number(!!b.is_coming_soon)).map((c) => (
                <div key={c.id} className="relative" data-testid={`course-tile-${c.slug}`}>
                  {showAll && (
                    <div className="absolute top-3 left-3 z-10">
                      <AudiencePill audiences={c.audiences} testid={`course-tile-audience-pill-${c.slug}`} />
                    </div>
                  )}
                  <CourseCard course={c} />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
